<script setup>
import { ref, watch, nextTick } from 'vue'
import { useForm } from '@inertiajs/vue3'
import AuthLayout from '@/Layouts/AuthLayout.vue'
import UiButton from '@/Components/UI/UiButton.vue'

const props = defineProps({
  errors: { type: Object, default: () => ({}) },
})

const form = useForm({
  email: '',
  password: '',
  remember_me: false,
})

// Local mirror of the server-rendered errors.base so we can clear it
// when the user starts typing again (without losing it on a stale prop
// re-render in dev).
const baseError = ref(props.errors?.base?.[0] || '')

// Shake the form when a new error arrives. Re-fires on every fresh
// failed submit by toggling a key.
const shakeNonce = ref(0)
watch(
  () => props.errors?.base?.[0],
  (msg) => {
    if (!msg) return
    baseError.value = msg
    nextTick(() => { shakeNonce.value++ })
  },
  { immediate: false }
)

function clearError() {
  if (baseError.value) baseError.value = ''
}

function submit() {
  form.transform((data) => ({ user: data })).post('/users/sign_in', {
    preserveScroll: true,
  })
}
</script>

<template>
  <AuthLayout title="Sign in to your account">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0 -translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="baseError"
        :key="`err-${shakeNonce}`"
        class="mb-4 flex items-center gap-2 rounded-[var(--radius-md)] p-3 text-sm shadow-sm"
        :style="{
          background: 'var(--dangerSoft)',
          border: '1px solid color-mix(in oklab, var(--danger) 35%, transparent)',
          color: 'var(--danger)'
        }"
        role="alert"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="shrink-0">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span class="flex-1">{{ baseError }}</span>
      </div>
    </Transition>

    <form
      :key="`form-${shakeNonce}`"
      :class="shakeNonce > 0 && baseError ? 'animate-login-shake' : ''"
      class="space-y-4"
      @submit.prevent="submit"
    >
      <div>
        <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Email</label>
        <input
          v-model="form.email"
          type="email"
          required
          autocomplete="email"
          class="w-full border rounded-[var(--radius-md)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)] transition-colors"
          :class="(form.errors.email || baseError) ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
          @input="clearError"
        />
        <p v-if="form.errors.email" class="mt-1 text-xs text-[color:var(--danger)]">{{ form.errors.email }}</p>
      </div>

      <div>
        <label class="block text-sm font-medium text-[color:var(--inkSoft)] mb-1">Password</label>
        <input
          v-model="form.password"
          type="password"
          required
          autocomplete="current-password"
          class="w-full border rounded-[var(--radius-md)] bg-[color:var(--surface)] text-[color:var(--ink)] px-3 py-2 text-sm focus:outline-2 focus:outline-[color:var(--accent)] transition-colors"
          :class="baseError ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
          @input="clearError"
        />
      </div>

      <div class="flex items-center justify-between">
        <label class="flex items-center gap-2 text-sm text-[color:var(--inkSoft)]">
          <input v-model="form.remember_me" type="checkbox" class="rounded" />
          Remember me
        </label>
        <a href="/users/password/new" class="text-sm text-[color:var(--accent)] hover:underline">
          Forgot password?
        </a>
      </div>

      <UiButton
        type="submit"
        variant="primary"
        :loading="form.processing"
        class="w-full justify-center"
      >
        {{ form.processing ? 'Signing in…' : 'Sign in' }}
      </UiButton>
    </form>
  </AuthLayout>
</template>

<style>
@keyframes login-shake {
  0%, 100% { transform: translateX(0); }
  20%      { transform: translateX(-6px); }
  40%      { transform: translateX(6px); }
  60%      { transform: translateX(-4px); }
  80%      { transform: translateX(4px); }
}
.animate-login-shake {
  animation: login-shake 0.4s ease-in-out;
}
</style>
